import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { financeApi, Payable } from '../../api/finance';
import { ApiError } from '../../api/client';
import { SectionHeader } from '../../components/SectionHeader';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { IconActionButton } from '../../components/IconActionButton';
import { Banknote } from 'lucide-react';
import { DataTable } from '../../components/DataTable';
import { useToast } from '../../components/Toast';

const STATUS_LABELS: Record<string, string> = { outstanding: 'Outstanding', partially_paid: 'Partially Paid', paid: 'Paid' };

export function PayablesPage() {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const payablesQuery = useQuery({ queryKey: ['finance-payables'], queryFn: () => financeApi.listPayables() });
  const fiscalYearsQuery = useQuery({ queryKey: ['finance-fiscal-years'], queryFn: () => financeApi.listFiscalYears() });

  const [payingId, setPayingId] = useState<number | null>(null);
  const [amount, setAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [fiscalYearId, setFiscalYearId] = useState<number | null>(null);
  const fiscalYearDetailQuery = useQuery({ queryKey: ['finance-fiscal-year-detail', fiscalYearId], queryFn: () => financeApi.getFiscalYear(fiscalYearId!), enabled: !!fiscalYearId });
  const [periodId, setPeriodId] = useState<number | null>(null);

  const payMutation = useMutation({
    mutationFn: () => financeApi.payPayable(payingId!, { amount: Number(amount), paymentDate, fiscalYearId: fiscalYearId!, accountingPeriodId: periodId! }),
    onSuccess: (result) => { showSuccess(`Payment recorded - journal ${result.data.journalReference}, now ${result.data.status}.`); queryClient.invalidateQueries({ queryKey: ['finance-payables'] }); setPayingId(null); setAmount(''); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to record payment - check Finance Settings has the Bank account configured.'),
  });

  const periods = fiscalYearDetailQuery.data?.periods ?? [];

  return (
    <div className="page">
      <PageHeader title="Accounts Payable" description="Amounts owed to staff for approved expense claims. Vendor/supplier payables will appear here once Procurement exists." />

      {payingId !== null && (
        <section className="card">
          <h2>Record Payment</h2>
          <label>Amount<input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} /></label>
          <label>Payment Date<input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} /></label>
          <label>
            Fiscal Year
            <select onChange={(e) => setFiscalYearId(Number(e.target.value) || null)}>
              <option value="">- select -</option>
              {fiscalYearsQuery.data?.data.map((fy) => <option key={fy.id} value={fy.id}>{fy.year_label}</option>)}
            </select>
          </label>
          <label>
            Accounting Period
            <select onChange={(e) => setPeriodId(Number(e.target.value) || null)}>
              <option value="">- select -</option>
              {periods.map((p) => <option key={p.id} value={p.id}>{p.period_label}</option>)}
            </select>
          </label>
          <div className="form-actions">
            <button type="button" className="btn-primary" onClick={() => payMutation.mutate()} disabled={payMutation.isPending || !amount || !periodId}>{payMutation.isPending ? 'Recording…' : 'Record Payment'}</button>
            <button type="button" className="btn-secondary" onClick={() => setPayingId(null)}>Cancel</button>
          </div>
        </section>
      )}

      <div className="panel">
        <SectionHeader title="All Payables" />
        <div className="panel-body">
        {payablesQuery.data && (
          <DataTable<Payable>
            rows={payablesQuery.data.data}
            rowKey={(p) => p.id}
            emptyMessage="No payables yet."
            columns={[
              { header: 'Reference', render: (p) => p.payable_reference },
              { header: 'Payee', render: (p) => p.payee_name ?? '-' },
              { header: 'Source', render: (p) => p.source_module },
              { header: 'Amount', render: (p) => Number(p.amount).toLocaleString() },
              { header: 'Paid', render: (p) => Number(p.amount_paid).toLocaleString() },
              { header: 'Outstanding', render: (p) => (Number(p.amount) - Number(p.amount_paid)).toLocaleString() },
              { header: 'Status', render: (p) => <StatusBadge status={p.status} label={STATUS_LABELS[p.status]} /> },
              { header: '', render: (p) => p.status !== 'paid' && <IconActionButton icon={Banknote} label="Record Payment" onClick={() => setPayingId(p.id)} /> },
            ]}
          />
        )}
      </div>
      </div>
    </div>
  );
}
